import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/** @type {Map<string, string>} */
const sectionFetchCache = new Map();

/** @type {Map<string, Promise<string>>} */
const sectionFetchInflight = new Map();

const SECTION_FETCH_CACHE_MAX = 24;

/**
 * @param {string} requestUrl
 * @param {string} responseText
 */
function rememberSectionFetch(requestUrl, responseText) {
  if (sectionFetchCache.has(requestUrl)) {
    sectionFetchCache.delete(requestUrl);
  }
  sectionFetchCache.set(requestUrl, responseText);
  while (sectionFetchCache.size > SECTION_FETCH_CACHE_MAX) {
    const oldest = sectionFetchCache.keys().next().value;
    if (typeof oldest === 'string') sectionFetchCache.delete(oldest);
    else break;
  }
}

/**
 * @param {string} requestUrl
 * @param {AbortSignal} signal
 * @returns {Promise<string>}
 */
function fetchSectionText(requestUrl, signal) {
  const cached = sectionFetchCache.get(requestUrl);
  if (cached) return Promise.resolve(cached);

  let inflight = sectionFetchInflight.get(requestUrl);
  if (inflight) {
    return inflight.then((text) => {
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      return text;
    });
  }

  inflight = fetch(requestUrl, { signal })
    .then((response) => response.text())
    .then((text) => {
      rememberSectionFetch(requestUrl, text);
      return text;
    })
    .finally(() => {
      sectionFetchInflight.delete(requestUrl);
    });

  sectionFetchInflight.set(requestUrl, inflight);
  return inflight;
}

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('click', this.#onNativeSizeClick, true);
    this.addEventListener('pointerdown', this.#onNativeSizePointerDown, true);
    this.addEventListener('pointerdown', this.#onNativeStylePointerDown, true);
    this.addEventListener('pointerenter', this.#onNativeSizePointerEnter, true);
    this.#resizeObserver.observe(this);
    queueMicrotask(() => {
      this.#warmSectionCacheForVisibleSizes();
      if (this.dataset.templateProductMatch === 'true') {
        const snapped = this.#ensureInStockSizeSelected({ reason: 'init' });
        if (!snapped) {
          this.#syncBuyButtonsIfVariantIdDrift();
        }
      }
    });
  }

  /**
   * First paint: Liquid snap may check a different size than buy-buttons variant id — refresh section once.
   * @returns {boolean}
   */
  #syncBuyButtonsIfVariantIdDrift() {
    if (this.#snapInProgress) return false;

    const sizeRow = this.querySelector('[data-jw-option-role="size"], .jw-seree-size, .jw-native-size');
    if (!sizeRow) return false;

    const checked = sizeRow.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement)) return false;
    if (checked.dataset.optionAvailable === 'false') return false;

    const expectedVariantId = checked.dataset.variantId;
    if (!expectedVariantId) return false;

    const section = this.closest('.shopify-section');
    const productForm = section?.querySelector('product-form-component');
    const hiddenId = productForm?.querySelector('input[name="id"]')?.value;
    if (hiddenId != null && String(hiddenId) === String(expectedVariantId)) return false;

    const {
      optionValueId = '',
      variantId = '',
      connectedProductUrl = '',
      optionName = '',
    } = checked.dataset;

    this.fetchUpdatedSection(this.buildRequestUrl(checked), {
      detail: {
        optionValueId,
        variantId,
        connectedProductUrl,
        optionName,
        optionRole: 'size',
      },
      isOnProductPage: true,
    });
    return true;
  }

  #snapInProgress = false;

  /**
   * When Style changes (or first paint without ?variant=), select the first in-stock Size for that Style.
   * Skipped when the shopper explicitly chose a Size (including sold-out → NOTIFY).
   * @param {{ reason?: 'init' | 'style' }} [options]
   * @returns {boolean}
   */
  #ensureInStockSizeSelected({ reason = 'init' } = {}) {
    if (this.#snapInProgress) return false;
    if (this.dataset.templateProductMatch !== 'true') return false;

    if (reason === 'init') {
      try {
        if (new URL(window.location.href).searchParams.has('variant')) return false;
      } catch {
        /* ignore */
      }
    }

    const sizeRow = this.querySelector('[data-jw-option-role="size"], .jw-seree-size, .jw-native-size');
    if (!sizeRow) return false;

    if (reason === 'style' && sizeRow.hasAttribute('data-jw-size-instock-only')) return false;
    // Shopper explicitly chose a sold-out Size for NOTIFY — do not snap away after Style change.
    if (reason === 'style' && sizeRow.dataset.jwUserPickedOosSize === 'true') return false;

    const checked = sizeRow.querySelector('input[type="radio"]:checked');
    if (!(checked instanceof HTMLInputElement)) return false;
    if (checked.dataset.optionAvailable !== 'false') return false;

    const firstAvail = sizeRow.querySelector('input[type="radio"][data-option-available="true"]');
    if (!(firstAvail instanceof HTMLInputElement) || firstAvail === checked) return false;

    this.#snapInProgress = true;
    checked.checked = false;
    checked.dataset.currentChecked = 'false';
    firstAvail.checked = true;
    firstAvail.dataset.currentChecked = 'true';

    sizeRow.querySelectorAll('.jw-seree-size__pill').forEach((pill) => {
      pill.classList.remove('is-selected');
    });
    const availPill = firstAvail.closest('.jw-seree-size__pill');
    availPill?.classList.add('is-selected');

    this.updateSelectedOption(firstAvail);
    this.#syncNativeOptionValueLabels();
    this.#emitPdpBuyState(firstAvail);

    const {
      optionValueId = '',
      variantId = '',
      connectedProductUrl = '',
      optionName = '',
    } = firstAvail.dataset;

    this.fetchUpdatedSection(this.buildRequestUrl(firstAvail), {
      detail: {
        optionValueId,
        variantId,
        connectedProductUrl,
        optionName,
        optionRole: 'size',
      },
      isOnProductPage: true,
    });

    queueMicrotask(() => {
      this.#snapInProgress = false;
    });
    return true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onNativeSizeClick, true);
    this.removeEventListener('pointerdown', this.#onNativeSizePointerDown, true);
    this.removeEventListener('pointerdown', this.#onNativeStylePointerDown, true);
    this.removeEventListener('pointerenter', this.#onNativeSizePointerEnter, true);
    this.#resizeObserver.disconnect();
  }

  /**
   * @param {HTMLInputElement} input
   */
  #emitPdpBuyState(input) {
    const section = this.closest('.shopify-section');
    if (!section) return;
    const productId = this.dataset.productId ?? '';
    const detail = {
      available: input.dataset.optionAvailable === 'true',
      variantId: input.dataset.variantId ?? '',
    };
    const forms = section.querySelectorAll('product-form-component');
    const pf =
      productId !== ''
        ? section.querySelector(`product-form-component[data-product-id="${productId}"]`)
        : forms.length === 1
          ? forms[0]
          : null;
    if (pf instanceof HTMLElement) {
      pf.dispatchEvent(new CustomEvent('jw:pdp-variant-buy-state', { bubbles: false, detail }));
      return;
    }
    section.dispatchEvent(new CustomEvent('jw:pdp-variant-buy-state', { bubbles: false, detail }));
  }

  /** Instant Size pill + sub-label feedback (do not wait for section fetch / morph). */
  #onNativeSizePointerDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const pill = event.target.closest('.jw-seree-size__pill');
    if (!pill) return;

    const fieldset = pill.closest('[data-jw-option-role="size"], .jw-seree-size, .jw-native-size');
    if (!fieldset) return;
    if (pill.classList.contains('is-unavailable') && fieldset.hasAttribute('data-jw-size-instock-only')) return;

    fieldset.querySelectorAll('.jw-seree-size__pill').forEach((el) => {
      el.classList.remove('is-selected');
    });
    pill.classList.add('is-selected');

    const valueEl =
      fieldset.querySelector('[data-jw-option-value]') ?? fieldset.querySelector('.jw-seree-size__value');
    const labelText = pill.querySelector('.jw-seree-size__pill-text')?.textContent?.trim();
    if (valueEl && labelText) {
      valueEl.textContent = labelText;
    }

  };

  /** Ensure OOS Size pills select + NOTIFY (click phase, after radio state updates). */
  #onNativeSizeClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const pill = event.target.closest('.jw-seree-size__pill');
    if (!pill || !this.contains(pill)) return;

    const fieldset = pill.closest('[data-jw-option-role="size"], .jw-seree-size, .jw-native-size');
    if (!fieldset) return;
    if (pill.classList.contains('is-unavailable') && fieldset.hasAttribute('data-jw-size-instock-only')) {
      event.preventDefault();
      return;
    }

    const input = pill.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return;

    if (!input.checked) {
      input.checked = true;
      this.updateSelectedOption(input);
      this.#syncNativeOptionValueLabels();
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    if (pill.classList.contains('is-unavailable')) {
      this.#emitPdpBuyState(input);
      if (this.dataset.templateProductMatch === 'true') {
        this.fetchUpdatedSection(this.buildRequestUrl(input), {
          detail: {
            optionValueId: input.dataset.optionValueId ?? '',
            variantId: input.dataset.variantId ?? '',
            connectedProductUrl: input.dataset.connectedProductUrl ?? '',
            optionName: input.dataset.optionName ?? '',
            optionRole: 'size',
          },
          isOnProductPage: true,
        });
      }
    }
  };

  /** Prefetch section HTML when hovering a Size pill (same request as click, cached). */
  #onNativeSizePointerEnter = (event) => {
    if (!(event.target instanceof Element)) return;

    const pill = event.target.closest('.jw-seree-size__pill');
    if (!pill) return;

    const fieldset = pill.closest('[data-jw-option-role="size"], .jw-seree-size, .jw-native-size');
    if (pill.classList.contains('is-unavailable') && fieldset?.hasAttribute('data-jw-size-instock-only')) return;

    const input = pill.querySelector('input[data-option-value-id]');
    if (!(input instanceof HTMLInputElement)) return;

    this.#prefetchSectionForOptionValueId(input.dataset.optionValueId ?? '');
  };

  /** After first paint, prefetch other Size combinations in the background. */
  #warmSectionCacheForVisibleSizes() {
    if (this.dataset.templateProductMatch !== 'true') return;

    const sizeRow = this.querySelector('.jw-seree-size, .jw-native-size, [data-jw-option-role="size"]');
    if (!sizeRow) return;

    const inputs = sizeRow.querySelectorAll('input[data-option-value-id]:not([aria-disabled="true"])');
    inputs.forEach((input, index) => {
      if (!(input instanceof HTMLInputElement) || input.checked) return;
      const id = input.dataset.optionValueId ?? '';
      if (!id) return;
      window.setTimeout(() => this.#prefetchSectionForOptionValueId(id), 120 + index * 80);
    });
  }

  /**
   * @param {string} optionValueId
   */
  #prefetchSectionForOptionValueId(optionValueId) {
    if (!optionValueId) return;

    const url = this.buildRequestUrlForOptionValueIds(this.#optionValueIdsReplacingSize(optionValueId));
    if (!url || sectionFetchCache.has(url)) return;

    fetchSectionText(url, new AbortController().signal).catch(() => {});
  }

  /**
   * @param {string} sizeOptionValueId
   * @returns {string[]}
   */
  #optionValueIdsReplacingSize(sizeOptionValueId) {
    const sizeRow = this.querySelector('.jw-seree-size, .jw-native-size, [data-jw-option-role="size"]');
    const ids = this.selectedOptionsValues;

    if (!sizeRow) return ids;

    const sizeInputs = sizeRow.querySelectorAll('input[data-option-value-id]');
    for (const input of sizeInputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const id = input.dataset.optionValueId ?? '';
      if (!id) continue;
      const index = ids.indexOf(id);
      if (index !== -1) {
        const next = ids.slice();
        next[index] = sizeOptionValueId;
        return next;
      }
    }

    return [...ids, sizeOptionValueId];
  }

  /** Instant Style sub-label from the swatch being pressed (before change / section morph). */
  #onNativeStylePointerDown = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.jw-native-style, [data-jw-option-role="style"]')) return;

    let radio = null;
    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      radio = event.target;
    } else {
      const label = event.target.closest('label[for]');
      const id = label?.getAttribute('for');
      if (id) radio = document.getElementById(id);
    }
    if (!(radio instanceof HTMLInputElement) || !radio.value) return;

    const fieldset = radio.closest('[data-jw-option-role="style"], .jw-native-style');
    fieldset?.querySelectorAll('.jw-native-style__choice').forEach((el) => {
      el.classList.remove('is-selected');
    });
    radio.closest('.jw-native-style__choice')?.classList.add('is-selected');

    const valueEl = fieldset?.querySelector('[data-jw-option-value]');
    if (valueEl) {
      valueEl.textContent = radio.value;
    }
  };

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.#syncNativeOptionValueLabels();

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const {
      optionValueId = '',
      variantId = '',
      connectedProductUrl = '',
      optionName = '',
      optionRole: optionRoleFromInput = '',
    } = selectedOption.dataset;
    const optionRole =
      optionRoleFromInput ||
      (selectedOption instanceof Element && selectedOption.closest('.jw-native-style, [data-jw-option-role="style"]')
        ? 'style'
        : selectedOption instanceof Element && selectedOption.closest('.jw-seree-size, .jw-native-size')
          ? 'size'
          : '');

    if (
      isOnProductPage &&
      optionRole === 'size' &&
      selectedOption instanceof HTMLInputElement
    ) {
      const sizeRow = selectedOption.closest('[data-jw-option-role="size"], .jw-seree-size, .jw-native-size');
      if (sizeRow instanceof HTMLElement) {
        sizeRow.dataset.jwUserPickedOosSize =
          selectedOption.dataset.optionAvailable === 'false' ? 'true' : 'false';
      }
      this.#emitPdpBuyState(selectedOption);
    }

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl, optionName, optionRole },
      isOnProductPage,
      loadsNewProduct,
      connectedProductUrlForPath: newUrl,
    });
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;
    if (fieldset.getAttribute('data-jw-option-role') === 'size') return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let optionValueIds = null;
    if (this.selectedOptionsValues.length && !source) {
      optionValueIds = this.selectedOptionsValues;
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        optionValueIds = sourceSelectedOptionsValues;
      }
    }
    return this.buildRequestUrlForOptionValueIds(optionValueIds, selectedOption, source, sourceSelectedOptionsValues);
  }

  /**
   * @param {string[] | null} optionValueIds
   * @param {HTMLElement} [selectedOption]
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string}
   */
  buildRequestUrlForOptionValueIds(
    optionValueIds,
    selectedOption = this.selectedOption ?? undefined,
    source = null,
    sourceSelectedOptionsValues = []
  ) {
    if (!selectedOption) return '';

    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (optionValueIds?.length) {
      params.push(`option_values=${optionValueIds.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    // PDP: render only the product-information section (not the full page HTML).
    const productSectionId = this.dataset.sectionId;
    if (productSectionId && this.dataset.templateProductMatch === 'true') {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${encodeURIComponent(productSectionId)}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string, optionName?: string, optionRole?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(
    requestUrl,
    { morphElementSelector, detail = {}, isOnProductPage = false, loadsNewProduct = false, connectedProductUrlForPath = '' } = {}
  ) {
    const { optionValueId = '', variantId, connectedProductUrl = '', optionName = '', optionRole = '' } = detail;
    const changedOptionRole = optionRole;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
          optionName,
          optionRole,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetchSectionText(requestUrl, this.#abortController.signal)
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        /** @type {NewProduct | undefined} */
        let newProduct;

        const applyMorph = () => {
          if (morphElementSelector === 'main') {
            this.updateMain(html);
            return;
          }
          if (morphElementSelector) {
            this.updateElement(html, morphElementSelector);
            return;
          }

          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          const sizeRow = this.querySelector('[data-jw-option-role="size"], .jw-seree-size, .jw-native-size');
          const hasNativeStyle = Boolean(
            this.querySelector('[data-jw-option-role="style"], .jw-native-style')
          );
          /** @type {string[]} */
          let preserveRoles = [];
          if (sizeRow && hasNativeStyle) {
            if (changedOptionRole === 'size') {
              preserveRoles = ['size', 'style'];
            } else if (
              sizeRow.hasAttribute('data-jw-size-instock-only') &&
              (changedOptionRole === 'style' || changedOptionRole === 'color')
            ) {
              // In-stock-only size rows depend on Style/Color; re-render sizes from section HTML.
              preserveRoles = ['style'];
            } else if (changedOptionRole === 'style') {
              // Re-render Style row so data-variant-id matches the current Size (preserving caused stale ids).
              preserveRoles = [];
            } else if (changedOptionRole === 'color') {
              preserveRoles = ['size', 'style'];
            }
          }
          newProduct = preserveRoles.length
            ? this.updateVariantPickerPreservingNativeRows(html, preserveRoles)
            : this.updateVariantPicker(html);

          if (wasSwatchesExpanded && overflowList instanceof OverflowList) {
            overflowList.showAll();
          }

          if (changedOptionRole === 'style') {
            queueMicrotask(() => {
              const snapped = this.#ensureInStockSizeSelected({ reason: 'style' });
              if (!snapped) {
                const sizeChecked = this.querySelector(
                  '[data-jw-option-role="size"] input[type="radio"]:checked, .jw-seree-size input[type="radio"]:checked'
                );
                if (sizeChecked instanceof HTMLInputElement) {
                  this.#emitPdpBuyState(sizeChecked);
                }
              }
            });
          }
        };

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object' && variantData.id) {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            this.#syncBrowserUrl({
              isOnProductPage,
              loadsNewProduct,
              connectedProductUrlForPath,
              variantId: String(variantData.id),
            });

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            // Let price / ATC / inventory update before heavy picker morph blocks the main thread.
            queueMicrotask(applyMorph);
            return;
          }
        }

        if (isOnProductPage) {
          this.#syncBrowserUrl({
            isOnProductPage,
            loadsNewProduct,
            connectedProductUrlForPath,
            variantId: '',
          });
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });

        queueMicrotask(applyMorph);
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @param {{ isOnProductPage?: boolean, loadsNewProduct?: boolean, connectedProductUrlForPath?: string, variantId?: string }} params
   */
  #syncBrowserUrl({ isOnProductPage, loadsNewProduct, connectedProductUrlForPath, variantId }) {
    if (!isOnProductPage) return;

    const url = new URL(window.location.href);

    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }

    if (loadsNewProduct && connectedProductUrlForPath) {
      url.pathname = connectedProductUrlForPath;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, this.#getVariantPickerMorphOptions());
    this.#rebuildFieldsetCaches();
    this.#syncNativeOptionValueLabels();
    this.updateVariantPickerCss();

    return newProduct;
  }

  /**
   * Morph picker but leave native rows (e.g. Size) untouched so click feedback is instant.
   * Still refreshes availability on preserved rows from server HTML.
   * @param {Document | Element} newHtml
   * @param {string[]} preserveRoles - data-jw-option-role values to skip morphing
   * @returns {NewProduct | undefined}
   */
  updateVariantPickerPreservingNativeRows(newHtml, preserveRoles) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    const preservedRows = preserveRoles.map((role) => ({
      role,
      oldRow: this.#findNativeOptionRow(this, role),
      newRow: this.#findNativeOptionRow(newVariantPickerSource, role),
    }));

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    const skipMarked = preservedRows
      .map(({ oldRow, newRow }) => [oldRow, newRow])
      .flat()
      .filter((node) => node instanceof HTMLElement);

    for (const row of skipMarked) {
      row.setAttribute('data-skip-subtree-update', '');
    }

    try {
      morph(this, newVariantPickerSource, this.#getVariantPickerMorphOptions(preserveRoles));
    } finally {
      for (const row of skipMarked) {
        if (row instanceof HTMLElement) {
          row.removeAttribute('data-skip-subtree-update');
        }
      }
    }

    for (const { oldRow, newRow } of preservedRows) {
      if (oldRow instanceof HTMLElement && newRow instanceof HTMLElement) {
        this.#syncNativeRowAvailabilityFromServer(oldRow, newRow);
      }
    }

    this.#rebuildFieldsetCaches();
    this.#syncNativeOptionValueLabels();
    this.updateVariantPickerCss();

    return newProduct;
  }

  /**
   * @param {string[]} [preserveRoles]
   * @returns {import('@theme/morph').Options}
   */
  #getVariantPickerMorphOptions(preserveRoles = []) {
    const preserve = new Set(preserveRoles);

    return {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        return node.dataset.key;
      },
      reject: (oldNode, newNode) => {
        if (MORPH_OPTIONS.reject?.(oldNode, newNode)) return true;

        const oldRole = this.#nativeOptionRoleOf(oldNode);
        const newRole = this.#nativeOptionRoleOf(newNode);
        if (oldRole && preserve.has(oldRole)) return true;
        if (newRole && preserve.has(newRole)) return true;
        return false;
      },
    };
  }

  /**
   * @param {ParentNode} root
   * @param {string} role
   * @returns {HTMLElement | null}
   */
  #findNativeOptionRow(root, role) {
    const byAttr = root.querySelector(`[data-jw-option-role="${role}"]`);
    if (byAttr instanceof HTMLElement) return byAttr;
    if (role === 'size') {
      const byClass = root.querySelector('.jw-seree-size, .jw-native-size');
      if (byClass instanceof HTMLElement) return byClass;
    }
    return null;
  }

  /**
   * @param {Node} node
   * @returns {string | null}
   */
  #nativeOptionRoleOf(node) {
    if (!(node instanceof HTMLElement)) return null;
    const explicit = node.getAttribute('data-jw-option-role');
    if (explicit) return explicit;
    if (node.classList.contains('jw-seree-size') || node.classList.contains('jw-native-size')) return 'size';
    return null;
  }

  /**
   * Copy sold-out / availability from a fresh server row onto the live row (inputs stay checked).
   * @param {HTMLElement} oldRow
   * @param {HTMLElement} newRow
   */
  #syncNativeRowAvailabilityFromServer(oldRow, newRow) {
    const newByValueId = new Map();
    newRow.querySelectorAll('input[data-option-value-id]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        newByValueId.set(input.dataset.optionValueId ?? '', input);
      }
    });

    oldRow.querySelectorAll('input[data-option-value-id]').forEach((oldInput) => {
      if (!(oldInput instanceof HTMLInputElement)) return;
      const fresh = newByValueId.get(oldInput.dataset.optionValueId ?? '');
      if (!fresh) return;

      const available = fresh.dataset.optionAvailable === 'true';
      const pill =
        oldInput.closest('.jw-seree-size__pill') ??
        oldInput.closest('.jw-native-text__pill') ??
        oldInput.closest('.jw-native-style__choice') ??
        oldInput.closest('label');
      pill?.classList.toggle('is-unavailable', !available);
      oldInput.dataset.optionAvailable = available ? 'true' : 'false';
      if (fresh.dataset.variantId) {
        oldInput.dataset.variantId = fresh.dataset.variantId;
      }
      const isSizeRow =
        oldRow.matches('[data-jw-option-role="size"], .jw-seree-size, .jw-native-size') ||
        Boolean(oldRow.querySelector('[data-jw-option-role="size"], .jw-seree-size, .jw-native-size'));
      const sizeInstockOnly =
        isSizeRow &&
        (oldRow.hasAttribute('data-jw-size-instock-only') ||
          Boolean(oldRow.closest('[data-jw-size-instock-only]')));
      const isStyleRow =
        oldRow.matches('[data-jw-option-role="style"], .jw-native-style') ||
        Boolean(oldRow.querySelector('[data-jw-option-role="style"], .jw-native-style'));

      if (available) {
        oldInput.removeAttribute('aria-disabled');
      } else if (sizeInstockOnly) {
        oldInput.setAttribute('aria-disabled', 'true');
      } else {
        oldInput.removeAttribute('aria-disabled');
      }
    });
  }

  /** Rebuild radio caches after morph (fieldsets/inputs may have been replaced). */
  #rebuildFieldsetCaches() {
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input[type="radio"]') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });
  }

  /** Sync grey sub-labels under each native option row from the checked control. */
  #syncNativeOptionValueLabels() {
    const fieldsets = this.querySelectorAll('[data-jw-option-role]');
    fieldsets.forEach((fieldset) => {
      const valueEl =
        fieldset.querySelector('[data-jw-option-value]') ?? fieldset.querySelector('.jw-seree-size__value');
      if (!valueEl) return;

      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (checked instanceof HTMLInputElement && checked.value) {
        valueEl.textContent = checked.value;
        return;
      }

      const select = fieldset.querySelector('select');
      if (select instanceof HTMLSelectElement && select.selectedOptions[0]) {
        valueEl.textContent = select.selectedOptions[0].textContent?.trim() ?? select.value;
      }

      if (fieldset.getAttribute('data-jw-option-role') === 'size') {
        fieldset.querySelectorAll('.jw-seree-size__pill').forEach((pill) => {
          pill.classList.toggle('is-selected', Boolean(pill.querySelector('input:checked')));
        });
      }
    });
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    // Prefer a visible checked radio (Size etc.). Hidden Color/Style fieldsets are
    // earlier in the DOM and would otherwise always win querySelector(:checked).
    const checked = Array.from(this.querySelectorAll('fieldset input:checked'));
    const visible = checked.find((input) => {
      const fieldset = input.closest('fieldset');
      return fieldset && !fieldset.classList.contains('visually-hidden') && !fieldset.classList.contains('variant-option--jw-hidden-option');
    });
    const selectedOption = visible || checked[checked.length - 1] || this.querySelector('select option[selected]');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    const nativeRoot = this.querySelector('.jw-variant-options');
    if (nativeRoot) {
      /** @type {string[]} */
      const ids = [];
      for (const fieldset of nativeRoot.querySelectorAll('fieldset')) {
        if (
          fieldset.classList.contains('visually-hidden') ||
          fieldset.classList.contains('variant-option--jw-hidden-option')
        ) {
          continue;
        }
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked instanceof HTMLInputElement && checked.dataset.optionValueId) {
          ids.push(checked.dataset.optionValueId);
        }
      }
      for (const select of nativeRoot.querySelectorAll('select')) {
        const selected = select.selectedOptions[0];
        if (selected?.dataset?.optionValueId) {
          ids.push(selected.dataset.optionValueId);
        }
      }
      if (ids.length) return ids;
    }

    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
